
import { useState, useEffect, useRef } from "react";
import { Message } from "../message-item";
import { useToast } from "@/hooks/use-toast";
import { WELCOME_MESSAGE } from "../constants";
import { useChatMessageFetcher } from "./use-chat-message-fetcher";
import { useChatMessageSender } from "./use-chat-message-sender";

export const useChatMessages = (threadId: string | null, onThreadIdChange: (threadId: string | null) => void) => {
  const [messages, setMessages] = useState<Message[]>([WELCOME_MESSAGE]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [hasPrivacyWarning, setHasPrivacyWarning] = useState(false);
  const [isProcessingLargeDataset, setIsProcessingLargeDataset] = useState(false);
  const { toast } = useToast();
  const [retryCount, setRetryCount] = useState(0);
  
  // Track if we're in an active conversation to prevent unnecessary resets
  const isActiveConversationRef = useRef(false);
  const previousThreadIdRef = useRef<string | null>(null);

  const { fetchMessagesForThread } = useChatMessageFetcher({
    setMessages,
    setIsLoading,
    setApiError,
    setRetryCount,
    retryCount,
    messages,
    onThreadIdChange,
    toast,
    WELCOME_MESSAGE
  });

  const { sendMessage } = useChatMessageSender({
    messages,
    setMessages,
    setIsLoading,
    setApiError,
    setHasPrivacyWarning,
    setIsProcessingLargeDataset,
    isLoading,
    isProcessingLargeDataset,
    threadId,
    onThreadIdChange,
    toast
  });

  // Enhanced threadId change handler that preserves messages during active conversations
  useEffect(() => {
    console.log('🔄 [USE-CHAT-MESSAGES] threadId changed:', threadId);
    console.log('🔄 [USE-CHAT-MESSAGES] previousThreadId:', previousThreadIdRef.current);
    console.log('🔄 [USE-CHAT-MESSAGES] isActiveConversation:', isActiveConversationRef.current);
    console.log('🔄 [USE-CHAT-MESSAGES] current messages count:', messages.length);
    
    const previousThreadId = previousThreadIdRef.current;
    
    if (threadId) {
      // Case 1: We have a threadId
      if (previousThreadId === null && messages.length > 1) {
        // We're transitioning from no thread to a thread during an active conversation
        // This happens when we get a threadId after sending a message
        console.log('🔗 [USE-CHAT-MESSAGES] Thread ID assigned during active conversation - preserving messages');
        isActiveConversationRef.current = true;
        // Don't reset messages, just update the ref
      } else if (previousThreadId !== threadId) {
        // We're switching to a different thread - fetch its messages
        console.log('📥 [USE-CHAT-MESSAGES] Switching to different thread - fetching messages');
        isActiveConversationRef.current = false;
        fetchMessagesForThread(threadId);
      }
      // If threadId is the same, do nothing (prevents unnecessary refetches)
    } else {
      // Case 2: No threadId (new conversation or reset)
      if (previousThreadId !== null || !isActiveConversationRef.current) {
        // Only reset if we're coming from a different thread or not in active conversation
        console.log('🏠 [USE-CHAT-MESSAGES] Starting new conversation - showing welcome message');
        setMessages([WELCOME_MESSAGE]);
        setApiError(null);
        setHasPrivacyWarning(false);
        setIsProcessingLargeDataset(false);
        isActiveConversationRef.current = false;
      }
      // If we're in an active conversation without a threadId, preserve the messages
    }
    
    // Update the previous threadId reference
    previousThreadIdRef.current = threadId;
  }, [threadId, fetchMessagesForThread]);

  // Track when messages change to detect active conversations
  useEffect(() => {
    if (messages.length > 1) {
      isActiveConversationRef.current = true;
    } else if (messages.length === 1 && messages[0].id === WELCOME_MESSAGE.id) {
      isActiveConversationRef.current = false;
    }
  }, [messages]);

  const resetMessages = () => {
    console.log('🔄 [USE-CHAT-MESSAGES] Resetting messages');
    setMessages([WELCOME_MESSAGE]);
    setApiError(null);
    setHasPrivacyWarning(false);
    setIsProcessingLargeDataset(false);
    isActiveConversationRef.current = false;
    previousThreadIdRef.current = null;
  };

  return {
    messages,
    isLoading,
    apiError,
    hasPrivacyWarning,
    isProcessingLargeDataset,
    sendMessage,
    fetchMessagesForThread,
    resetMessages
  };
};
